import {
  NINETY_DAY_TRIAL_ENDING_REMINDER_ACTION,
  TRIALS_AND_UPGRADES_SCREEN,
  type NinetyDayTrialEndingReminderActionToken,
} from '@heidi/commerce/contracts';
import { useReverseTrial, useUserTier } from '@heidi/commerce/data';
import { Button } from '@heidi/design-system/button';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { ModalId, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useSetAtom } from 'jotai';
import { X } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getCommerceBillingUiPorts } from '../../settings/billing/billing-ui-ports';
import { trialRecapModalOpenAtom } from '../trial-recap-modal/atoms';
import { getCommerceTrialsUiPorts } from '../trials-ui-ports';
import { getTeamContextAnalyticsProps } from '../utils/getTeamContextAnalyticsProps';
import { trialEndReminderPopupMessages } from './trial-end-reminder-popup.messages';

/** Time the popover stays on screen before auto-dismissing without engagement. */
export const TRIAL_END_REMINDER_AUTO_DISMISS_MS = 2 * 60 * 60 * 1000; // 2 hours

type AutoDismissAnalyticsSnapshot = {
  hasTeam: boolean;
  isLowUsage: boolean;
  isTeamAdmin: boolean | undefined;
  isTeamStripeManaged: boolean;
  userTier: SubscriptionTier | undefined;
};

/**
 * Floating reminder popover surfaced near the end of a user's trial.
 *
 * Subscribes to the modal queue via `ModalId.NINETY_DAY_TRIAL_ENDING_SOON_REMINDER_POPUP`
 * and renders a non-modal `Popover` anchored to the bottom-left corner of the
 * viewport. Shows the days remaining, the tier name, and a CTA that closes the
 * popover and opens the trial recap modal with the full usage summary.
 */
export const TrialEndReminderPopup = () => {
  const { isOpen, handleClose } = getCommerceTrialsUiPorts().useQueuedModal(
    ModalId.NINETY_DAY_TRIAL_ENDING_SOON_REMINDER_POPUP
  );

  const { isLowUsage, isUsageStatsPending, isTeamAdmin } =
    getCommerceTrialsUiPorts().useTrialRecapUsageStats(isOpen);
  const { hasTeam, isTeamStripeManaged } = useCombinedQueriesTeamGeneralInfo();

  // Don't permanently suppress a queued reminder if usage stats fail to load,
  // but also don't open mid-fetch.
  const effectiveOpen = isOpen && !isUsageStatsPending;

  const { clinicianTrialDaysLeft: daysLeft } = useReverseTrial();
  const { userTier } = useUserTier();
  const setTrialRecapModalOpen = useSetAtom(trialRecapModalOpenAtom);

  const intl = useIntl();
  const isClinicianPlan = userTier === SubscriptionTier.CLINICIAN_PRO;
  const clinicianPlanName = intl.formatMessage(
    trialEndReminderPopupMessages.clinicianPlanName
  );
  const practicePlanName = intl.formatMessage(
    trialEndReminderPopupMessages.practicePlanName
  );
  const evidenceFeatureName = intl.formatMessage(
    trialEndReminderPopupMessages.evidenceFeatureName
  );

  const tierName = isClinicianPlan ? clinicianPlanName : practicePlanName;
  const featureName = isClinicianPlan ? clinicianPlanName : evidenceFeatureName;

  const TierIcon = isClinicianPlan ? TierProFilled : TierPracticeFilled;

  useEffect(() => {
    if (!effectiveOpen) return;
    getCommerceTrialsUiPorts().analytics.trackNinetyDayTrialEndingReminderViewed({
      screen_name: TRIALS_AND_UPGRADES_SCREEN.ninetyDayTrialEndingSoonReminder,
      is_low_usage: isLowUsage,
      ...(userTier !== undefined && { current_plan: userTier }),
      ...getTeamContextAnalyticsProps({
        hasTeam,
        isTeamAdmin,
        isTeamStripeManaged,
      }),
    });
    // Only fire when popup open state changes; usage and plan are read at open-time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOpen]);

  const autoDismissAnalyticsRef = useRef<AutoDismissAnalyticsSnapshot | null>(null);

  useEffect(() => {
    if (!effectiveOpen) {
      autoDismissAnalyticsRef.current = null;
      return;
    }
    autoDismissAnalyticsRef.current = {
      hasTeam,
      isLowUsage,
      isTeamAdmin,
      isTeamStripeManaged,
      userTier,
    };
    // Snapshot at open-time only — keeps the 2-hour auto-dismiss timer stable when
    // tier/team props resolve after the popover first appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOpen]);

  useEffect(() => {
    if (!effectiveOpen) return;
    const timeoutId = setTimeout(() => {
      const snapshot = autoDismissAnalyticsRef.current;
      if (!snapshot) return;
      getCommerceTrialsUiPorts().analytics.trackNinetyDayTrialEndingReminderDismissed({
        screen_name: TRIALS_AND_UPGRADES_SCREEN.ninetyDayTrialEndingSoonReminder,
        auto_dismissed: true,
        dismiss_after_ms: TRIAL_END_REMINDER_AUTO_DISMISS_MS,
        is_low_usage: snapshot.isLowUsage,
        ...(snapshot.userTier !== undefined && { current_plan: snapshot.userTier }),
        ...getTeamContextAnalyticsProps({
          hasTeam: snapshot.hasTeam,
          isTeamAdmin: snapshot.isTeamAdmin,
          isTeamStripeManaged: snapshot.isTeamStripeManaged,
        }),
      });
      handleClose();
    }, TRIAL_END_REMINDER_AUTO_DISMISS_MS);
    return () => clearTimeout(timeoutId);
    // Redux dispatch is referentially stable; omitting it avoids resetting the timer on unrelated renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOpen, handleClose]);

  const trackReminderButton = useCallback(
    (action: NinetyDayTrialEndingReminderActionToken, label: string) => {
      getCommerceTrialsUiPorts().analytics.trackNinetyDayTrialEndingReminderClick({
        screen_name: TRIALS_AND_UPGRADES_SCREEN.ninetyDayTrialEndingSoonReminder,
        action,
        label,
        ...(userTier !== undefined && { current_plan: userTier }),
      });
    },
    [userTier]
  );

  const handleSeeRecap = useCallback(() => {
    trackReminderButton(
      NINETY_DAY_TRIAL_ENDING_REMINDER_ACTION.seeSummary,
      intl.formatMessage(
        isLowUsage
          ? trialEndReminderPopupMessages.takeALook
          : trialEndReminderPopupMessages.seeRecap
      )
    );
    setTrialRecapModalOpen(true);
    handleClose();
  }, [handleClose, intl, setTrialRecapModalOpen, trackReminderButton, isLowUsage]);

  const handleCloseClick = useCallback(() => {
    trackReminderButton(
      NINETY_DAY_TRIAL_ENDING_REMINDER_ACTION.close,
      intl.formatMessage(trialEndReminderPopupMessages.close)
    );
    handleClose();
  }, [handleClose, intl, trackReminderButton]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        handleClose();
      }
    },
    [handleClose]
  );

  const descriptionAdmin = intl.formatMessage(
    trialEndReminderPopupMessages.descriptionTeamAdmin,
    { planName: tierName }
  );
  const descriptionNonAdminNoUsage = intl.formatMessage(
    trialEndReminderPopupMessages.descriptionNonAdminNoUsage,
    { planName: tierName }
  );
  const description = intl.formatMessage(trialEndReminderPopupMessages.description, {
    tierName,
  });

  const trialEndReminderDescription =
    isTeamAdmin === true
      ? descriptionAdmin
      : isLowUsage
        ? descriptionNonAdminNoUsage
        : description;

  return (
    <Popover open={effectiveOpen} onOpenChange={handleOpenChange} modal={false}>
      <PopoverAnchor
        aria-hidden
        className="pointer-events-none fixed bottom-4 left-4 z-[55] size-px"
      />
      <PopoverContent
        side="top"
        align="start"
        sideOffset={0}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        aria-label={intl.formatMessage(trialEndReminderPopupMessages.ariaLabel)}
        data-testid="trial-end-reminder-popup"
        className={cn(
          'max-w-[260px] overflow-hidden rounded-xl border-0 bg-surface-container p-0 shadow-sheet',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
        )}
      >
        <div className="flex flex-col gap-2 py-3">
          <div className="flex flex-col gap-1">
            <div className="flex gap-2 px-4 py-0.5">
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <TypographyP2SemiBold className="min-w-0 flex-1 leading-normal text-text-primary">
                  <FormattedMessage
                    {...trialEndReminderPopupMessages.title}
                    values={{ daysLeft, featureName }}
                  />
                </TypographyP2SemiBold>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="xxs"
                iconOnly
                aria-label={intl.formatMessage(trialEndReminderPopupMessages.close)}
                onClick={handleCloseClick}
                className="shrink-0 text-text-secondary hover:bg-surface-hover hover:text-text-primary"
              >
                <X />
              </Button>
            </div>
            <div className="px-4">
              <TypographyCaption className="leading-snug text-text-secondary">
                {trialEndReminderDescription}
              </TypographyCaption>
            </div>
          </div>

          {!getCommerceBillingUiPorts().isAppleCompliantBuild() && (
            <div className="px-4 pb-1 pt-0">
              <Button
                type="button"
                variant={isClinicianPlan ? 'pro' : 'pro_plus'}
                size="xs"
                onClick={handleSeeRecap}
                className="w-full"
              >
                <TierIcon />
                <FormattedMessage
                  {...(isLowUsage && !isTeamAdmin
                    ? trialEndReminderPopupMessages.takeALook
                    : trialEndReminderPopupMessages.seeRecap)}
                />
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
